import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';
import { useWindowSize } from '../../../../common/hooks/WindowSize';
import { useOpenGlobalModal } from '../../../recoil/hooks/useOpenGlobalModal';

import { AccessConfig, AccessType } from '../../../types/user';
import { accessTableColumns, accessTableColumnsWidth, accessTableStickyColumnWidth } from './accessTableColumns';


interface AccessTableProps {
  accessOptions: Record<AccessType, AccessConfig>;
  selectedAccessType: AccessType | undefined;
  setSelectedAccessType: (accessType: AccessType) => void;
  setCurrentCustomAccessConfig: (accessConfig: AccessConfig) => void;
}


export const AccessTable: React.FC<AccessTableProps> = ({
  accessOptions,
  selectedAccessType,
  setSelectedAccessType,
  setCurrentCustomAccessConfig,
}) => {

  const userConfig = useRecoilValue(userConfigState);
  const { width } = useWindowSize();
  const { openTextModal } = useOpenGlobalModal();


  const tableWidth = Math.min(1500, width * 0.95) - 30;
  const accessTableNonStickyWidth = tableWidth - accessTableStickyColumnWidth;
  const accessTableNonStickyMinWidth = accessTableColumnsWidth;
  const widthUnit = Math.max(accessTableNonStickyWidth / accessTableNonStickyMinWidth, 1);

  const tableInnerWidth = Math.max(tableWidth, accessTableStickyColumnWidth + accessTableColumnsWidth);

  return (
    <div
      className='table-container season-table-container'
      style={{
        width: tableWidth,
        height: ((Object.keys(accessOptions).length + 1) * 50) + (widthUnit <= 1 ? 15 : 0),
        overflowX: widthUnit <= 1 ? 'auto' : 'hidden',
        overflowY: 'hidden',
        border: '0.5px solid #616a7b',
        fontSize: 12,
      }}
    >


      {/* Header */}
      <div
        className='table-header access-table-row access-table-header-background'
        style={{ width: tableInnerWidth }}
      >
        {accessTableColumns.map((column) => (
          <div
            key={column.key}
            className={
              'table-cell'
              + (column.isStickyColumn ? ' table-sticky-left access-table-sticky-cell access-table-header-background' : '')
            }
            style={{ width: column.width * (column.isStickyColumn ? 1 : widthUnit), minWidth: column.width * (column.isStickyColumn ? 1 : widthUnit) }}
          >
            {column.header({ openTextModal, language: userConfig?.language })}
          </div>
        ))}
      </div>

      {/* Body */}
      {Object.values(accessOptions).map((accessConfig, rowIndex) => {

        const isSelected = accessConfig.accessType === selectedAccessType;

        return (
          <div
            key={accessConfig.accessType}
            className={
              'access-table-row'
              + (rowIndex % 2 === 0 ? ' access-table-row-even' : ' access-table-row-odd')
              + (accessConfig.accessType === selectedAccessType ? ' access-table-row-selected' : '')
            }
            style={{ width: tableInnerWidth }}
            onClick={() => setSelectedAccessType(accessConfig.accessType)}
          >
            {accessTableColumns.map((column) => (
              <div
                key={column.key}
                className={
                  'table-cell'
                  + (column.isStickyColumn
                    ? (
                      ' table-sticky-left access-table-sticky-cell'
                      + (isSelected
                        ? ' access-table-row-selected'
                        : (rowIndex % 2 === 0 ? ' access-table-sticky-cell-even' : ' access-table-sticky-cell-odd'))
                    )
                    : '')
                }
                style={{ width: column.width * (column.isStickyColumn ? 1 : widthUnit), minWidth: column.width * (column.isStickyColumn ? 1 : widthUnit) }}
              >
                {column.cell({
                  row: accessConfig,
                  setCurrentCustomAccessConfig: accessConfig.accessType === 'custom' ? setCurrentCustomAccessConfig : undefined,
                  language: userConfig?.language
                })}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};
